<template>
    <ul class="user-verifications-and-stats">
        <li v-if="user.isIdVerified && !showStats" class="user-verifications-and-stats__item">
            <svg-icon class="verification__icon" name="account_check_outline" />
            <span class="verification__text">
                {{ $t('user_verifications.id_verified') }}
            </span>
        </li>
        <li v-if="userIsRenter" class="user-verifications-and-stats__item verification">
            <template v-if="user.creditReportResult === RESULT_OK">
                <svg-icon class="verification__icon" name="verified_user" />
                <div class="verification__text">
                    {{ $t('user_verifications.credit_report_ok') }}
                </div>
            </template>
            <template v-else-if="user.creditReportResult > 0">
                <svg-icon class="verification__icon verification__icon--warning" name="warning" />
                <div class="verification__text">
                    {{ $t('user_verifications.credit_report_not_ok') }}
                    <a :href="$t('links.faq')" target="_blank" class="verification__link">
                        {{ $t('user_verifications.credit_report_faq_link') }}
                    </a>
                </div>
            </template>
            <template v-else-if="user.idVerifiedManually">
                <svg-icon class="verification__icon" name="info" />
                <div class="verification__text">
                    {{ $t('user_verifications.credit_report_not_possible') }}
                </div>
            </template>
            <template v-else>
                <svg-icon class="verification__icon verification__icon--warning" name="warning" />
                <div class="verification__text">
                    {{ $t('user_verifications.credit_report_not_done') }}
                </div>
            </template>
        </li>
        <li v-if="showEmail && user.isEmailVerified" class="user-verifications-and-stats__item verification">
            <svg-icon class="verification__icon" name="verified_user" />
            <span class="verification__text">
                {{ $t('user_verifications.email_verified') }}
            </span>
        </li>
        <li v-if="showFacebook && user.facebookId" class="user-verifications-and-stats__item">
            <svg-icon class="verification__icon" name="verified_user" />
            <i18n path="user_verifications.fb_verified" class="verification__text">
                <a target="_blank" :href="facebookProfileUrl">{{ $t('user_verifications.fb_profile_link') }}</a>
            </i18n>
        </li>
        <template v-if="showStats && userIsOwner">
            <li v-if="isLoadingStats">
                <LoadingIndicator />
            </li>
            <template v-else-if="userStats">
                <li
                    v-if="userStats.topOwner"
                    class="user-verifications-and-stats__item verification verification--top-owner"
                >
                    <svg-icon class="verification__icon" name="top_owner" />
                    <span class="verification__text">
                        {{ $t('user_verifications.top_owner') }}
                    </span>
                </li>
                <li v-if="user.isIdVerified" class="user-verifications-and-stats__item">
                    <svg-icon class="verification__icon" name="account_check_outline" />
                    <span class="verification__text">
                        {{ $t('user_verifications.id_verified') }}
                    </span>
                </li>
                <li v-if="userStats.messageResponseTime !== null" class="user-verifications-and-stats__item">
                    <svg-icon class="verification__icon" name="response_time" />
                    <span class="verification__text">
                        {{
                            $t('user_verifications.message_response_time', {
                                time: messageResponseTimeLabel,
                            })
                        }}
                    </span>
                </li>
                <li v-if="userStats.bookingAcceptanceRate !== null" class="user-verifications-and-stats__item">
                    <svg-icon class="verification__icon" name="acceptance_rate" />
                    <span class="verification__text">
                        {{
                            $t('user_verifications.booking_acceptance_rate', {
                                rate: Math.round(userStats.bookingAcceptanceRate * 100),
                            })
                        }}
                    </span>
                </li>
            </template>
        </template>
        <li v-if="bookingHasBeenConfirmed" class="user-verifications-and-stats__item">
            <svg-icon class="verification__icon" name="phone" />
            <a v-if="userPhone" :href="'tel:' + userPhone">{{ userPhone }}</a>
            <span v-else class="verification__phone-number-text">{{
                $t('pages.booking.user_stats.no_phone_number')
            }}</span>
        </li>
        <li v-if="isRenter && gmapUrl" class="user-verifications-and-stats__item">
            <svg-icon class="verification__icon" name="location" />
            <div class="item-column-wrapper">
                <span class="verification__text">{{ $t('pages.booking.user_stats.vehicle_location_label') }}</span>
                <a :href="gmapUrl" :target="linkTarget">
                    <div class="column-item">{{ vehicleAddress }}</div>
                    <div class="column-item">{{ vehicleCity }}</div>
                </a>
            </div>
        </li>
        <li
            v-if="displayConversationToggle"
            class="user-verifications-and-stats__item"
            @click="$emit('toggle-conversation')"
        >
            <svg-icon class="verification__icon" name="chat" />
            <div class="item-column-wrapper">
                <a>{{ conversationLabel }}</a>
                <div v-if="displayNewMessageIndicator" class="column-item">
                    <span>{{ $t('pages.booking.user_stats.new_message_label') }}</span>
                    <span class="new-message-indicator" />
                </div>
            </div>
        </li>
    </ul>
</template>

<script>
import { mapGetters } from 'vuex';

import { parsePhoneNumber } from 'libphonenumber-js/mobile';

import LoadingIndicator from '~/components/LoadingIndicator.vue';
import { RESULT_OK } from '~/config/creditReportResults';

export default {
    components: { LoadingIndicator },
    props: {
        user: {
            type: Object,
            default: () => {},
        },
        showEmail: {
            type: Boolean,
            default: false,
        },
        showFacebook: {
            type: Boolean,
            default: false,
        },
        showStats: {
            type: Boolean,
            default: false,
        },
        conversationOpened: {
            type: Boolean,
            default: false,
        },
        vehicle: {
            type: Object,
            default: () => ({}),
        },
        displayNewMessageIndicator: {
            type: Boolean,
            default: false,
        },
        displayConversationToggle: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            userStats: null,
            isLoadingStats: false,

            RESULT_OK,
        };
    },
    computed: {
        ...mapGetters(['isRenter', 'bookingHasBeenConfirmed']),
        linkTarget() {
            return this.isLargeScreen ? '_blank' : '_self';
        },
        facebookProfileUrl() {
            return `https://facebook.com/profile.php?id=${this.user.facebookId}`;
        },
        userIsRenter() {
            // return this.user.role === 'renter';
            // will be returned after danish user verification
            return false;
        },
        userIsOwner() {
            return this.user.role === 'owner';
        },
        messageResponseTimeLabel() {
            if (!this.userIsOwner || !this.userStats || !this.userStats.messageResponseTime) {
                return null;
            }

            const seconds = this.userStats.messageResponseTime;

            if (seconds < 60 * 30) {
                return this.$t('user_verifications.response_within_half_an_hour');
            }

            if (seconds < 60 * 60) {
                return this.$t('user_verifications.response_within_an_hour');
            }

            if (seconds < 60 * 60 * 2) {
                return this.$t('user_verifications.response_within_two_hours');
            }

            if (seconds < 60 * 60 * 8) {
                return this.$t('user_verifications.response_within_a_few_hours');
            }

            if (seconds < 60 * 60 * 24) {
                return this.$t('user_verifications.response_within_a_day');
            }

            if (seconds < 60 * 60 * 48) {
                return this.$t('user_verifications.response_within_two_days');
            }

            return this.$t('user_verifications.response_more_than_two_days');
        },
        conversationLabel() {
            return this.conversationOpened
                ? this.$t('pages.booking.user_stats.hide_conversation')
                : this.$t('pages.booking.user_stats.show_conversation');
        },
        vehicleAddress() {
            if (!this.vehicle.address) {
                return '';
            }

            return this.vehicle.address;
        },
        vehicleCity() {
            if (!this.vehicle.city && !this.vehicle.zipCode) {
                return '';
            }

            if (!this.vehicle.city) {
                return this.$t(`localization.countries.${this.vehicle.country}`);
            }

            if (this.vehicle.city && !this.vehicle.zipCode) {
                return `${this.vehicle.city}`;
            }

            return `${this.vehicle.zipCode} ${this.vehicle.city}`;
        },
        userPhone() {
            if (!this.user.phone || !this.user.country) {
                return null;
            }

            const phoneNumber = parsePhoneNumber(this.user.phone, this.user.country);

            return phoneNumber.number;
        },
        gmapUrl() {
            if (!this.vehicle.longitude || !this.vehicle.latitude) {
                return '';
            }

            return `https://www.google.com/maps/place/${this.vehicle.latitude},${this.vehicle.longitude}`;
        },
    },
    created() {
        if (this.showStats) {
            this.isLoadingStats = true;

            this.$userService
                .getStats(this.user.id)
                .then(stats => {
                    this.userStats = stats;
                })
                .catch(err => {
                    this.$sentry.captureException(err);
                })
                .finally(() => {
                    this.isLoadingStats = false;
                });
        }
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.user-verifications-and-stats {
    width: 100%;
    text-align: left;
    &__item {
        display: flex;

        &:not(:last-child) {
            margin-bottom: 10px;
        }

        .item-column-wrapper {
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            .column-item {
                position: relative;
                line-height: 24px;

                .new-message-indicator {
                    width: 12px;
                    height: 12px;
                    position: absolute;
                    top: -2px;
                    background-color: $warning;
                    border-radius: 50%;
                }
            }
        }
    }
}

.verification {
    &__icon.svg-icon {
        min-width: 24px;
        min-height: 24px;
        margin-right: 5px;
        color: $primary;
    }

    &__icon--warning.svg-icon {
        color: $error;
    }

    &__icon--incomplete.svg-icon {
        color: $disabled;
    }

    &__text,
    &__link {
        display: inline-block;
        line-height: 1.3em;
    }

    &__phone-number-text {
        display: block;
        width: calc(100% - 116px);
        @include font-size(14px);
    }

    &--top-owner {
        .verification__icon.svg-icon {
            min-width: 28px;
            min-height: 28px;
            margin-left: -2px;
            margin-top: -2px;
            margin-right: 3px;
        }

        .verification__text {
            font-weight: 600;
        }
    }
}
</style>
